/**
 * PrimeReact Pass-Through (PT) configuration
 *
 * Maps every PrimeReact component used in the merchant portal
 * to Tailwind classes that match our design token system.
 *
 * Severity / variant classNames are resolved at render time via
 * PrimeReact's `context` argument (e.g. context.props.severity).
 */

import { classNames } from "primereact/utils";

/* ─── Helpers ─── */

const severityButton = (context: any) => {
    const s = context?.props?.severity;
    const outlined = context?.props?.outlined;
    const text = context?.props?.text;
    const size = context?.props?.size;
    const isIconOnly = context?.props?.icon && !context?.props?.label;

    const base =
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

    const sizeClass = isIconOnly
        ? size === "small"
            ? "p-1.5 text-sm"
            : "p-2.5 text-sm"
        : size === "small"
            ? "px-3 py-1.5 text-sm"
            : size === "large"
              ? "px-6 py-3 text-lg"
              : "px-4 py-2.5 text-sm";

    if (text) {
        return classNames(base, sizeClass, {
            "text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20": !s || s === "info",
            "text-success-600 hover:bg-success-50 dark:text-success-400 dark:hover:bg-success-900/20": s === "success",
            "text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/20": s === "danger",
            "text-warning-600 hover:bg-warning-50 dark:text-warning-400 dark:hover:bg-warning-900/20": s === "warning",
            "text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800": s === "secondary",
        });
    }

    if (outlined) {
        return classNames(base, sizeClass, "border bg-transparent", {
            "border-primary-500 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20":
                !s || s === "info",
            "border-success-500 text-success-600 hover:bg-success-50 dark:border-success-400 dark:text-success-400":
                s === "success",
            "border-danger-500 text-danger-600 hover:bg-danger-50 dark:border-danger-400 dark:text-danger-400":
                s === "danger",
            "border-warning-500 text-warning-600 hover:bg-warning-50 dark:border-warning-400 dark:text-warning-400":
                s === "warning",
            "border-neutral-300 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-800":
                s === "secondary",
        });
    }

    // Solid (default)
    return classNames(base, sizeClass, {
        "bg-primary-600 text-white hover:bg-primary-700 hover:shadow-md focus:ring-primary-500 dark:bg-primary-500 dark:hover:bg-primary-600":
            !s || s === "info",
        "bg-success-600 text-white hover:bg-success-700 hover:shadow-md focus:ring-success-500": s === "success",
        "bg-danger-600 text-white hover:bg-danger-700 hover:shadow-md focus:ring-danger-500": s === "danger",
        "bg-warning-600 text-white hover:bg-warning-700 hover:shadow-md focus:ring-warning-500": s === "warning",
        "bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50 focus:ring-neutral-500 dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-700":
            s === "secondary",
    });
};

/* ─── Pass-Through Object ─── */

export const merchantPortalPT = {
    /* ── Button ── */
    button: {
        root: ({ context }: any) => ({
            className: severityButton(context),
        }),
        icon: {
            className: "text-current",
        },
        loadingIcon: {
            className: "animate-spin",
        },
        label: {
            className: "font-medium",
        },
    },

    /* ── DataTable ── */
    datatable: {
        root: {
            className:
                "relative bg-white dark:bg-neutral-900 overflow-hidden max-w-full",
        },
        wrapper: {
            className: "overflow-auto",
        },
        loadingOverlay: {
            className:
                "absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-neutral-900/70 backdrop-blur-sm z-10 rounded-xl",
        },
        loadingIcon: {
            className: "w-8 h-8 animate-spin text-primary-500",
        },
        header: {
            className:
                "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 font-semibold empty:hidden",
        },
        table: {
            className: "w-full border-spacing-0",
        },
        thead: {
            className: "",
        },
        headerRow: {
            className: "",
        },
        tbody: {
            className: "bg-white dark:bg-neutral-900",
        },
        bodyRow: ({ context }: any) => ({
            className: classNames(
                "border-b border-neutral-200 dark:border-neutral-800 transition-colors duration-150 cursor-pointer",
                {
                    "hover:bg-neutral-50 dark:hover:bg-neutral-800/50": !context?.selected,
                    "bg-primary-50 dark:bg-primary-900/20": context?.selected,
                },
            ),
        }),
        footer: {
            className:
                "bg-neutral-50 dark:bg-neutral-800/50 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800",
        },
        emptyMessage: {
            className: "",
        },
        emptyMessageCell: {
            className: "text-center",
        },
        filterRow: {
            className: "bg-neutral-50/50 dark:bg-neutral-800/30",
        },
        /* Column-level PT keys (headerCell, bodyCell, sort, filter overlay, etc.) */
        column: {
            headerCell: ({ context }: any) => ({
                className: classNames(
                    "text-left align-middle px-6 py-3 bg-neutral-50 dark:bg-neutral-900/50 text-xs font-medium uppercase tracking-wider text-neutral-700 dark:text-neutral-300",
                    {
                        "cursor-pointer select-none hover:bg-neutral-100 dark:hover:bg-neutral-700/50":
                            context?.sorted !== undefined,
                    },
                ),
            }),
            bodyCell: {
                className:
                    "align-middle px-6 py-4 text-sm text-neutral-900 dark:text-neutral-100",
            },
            sortIcon: ({ context }: any) => ({
                className: classNames("ml-1 text-xs", {
                    "text-primary-500": context?.sorted,
                    "text-neutral-400": !context?.sorted,
                }),
            }),
            sortBadge: {
                className:
                    "inline-flex items-center justify-center ml-1 w-5 h-5 text-xs font-bold rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400",
            },
            filterInput: {
                className:
                    "w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none",
            },
            filterMenuButton: ({ context }: any) => ({
                className: classNames(
                    "inline-flex items-center justify-center w-7 h-7 ml-1 rounded-md transition-colors cursor-pointer",
                    {
                        "text-primary-500 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30":
                            context?.active,
                        "text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800":
                            !context?.active,
                    },
                ),
            }),
            headerFilterClearButton: ({ context }: any) => ({
                className: classNames(
                    "inline-flex items-center justify-center w-7 h-7 ml-1 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer",
                    { invisible: !context?.hidden },
                ),
            }),
            columnFilter: {
                className: "inline-flex items-center ml-auto flex-shrink-0",
            },
            filterOverlay: {
                className:
                    "bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded-xl shadow-xl min-w-[14rem] ring-1 ring-neutral-200 dark:ring-neutral-700",
            },
            filterOperator: {
                className:
                    "px-4 py-3 border-b border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-neutral-800/50 rounded-t-xl",
            },
            filterOperatorDropdown: {
                root: { className: "min-[0px]:flex" },
            },
            filterConstraints: {
                className: "",
            },
            filterConstraint: {
                className: "p-4 border-b border-neutral-100 dark:border-neutral-700/50",
            },
            filterMatchModeDropdown: {
                root: { className: "min-[0px]:flex mb-2" },
            },
            filterRemove: {
                className: "mt-2",
            },
            filterRemoveButton: {
                root: { className: "ml-2" },
                label: { className: "grow-0" },
            },
            filterAddRule: {
                className: "py-3 px-4",
            },
            filterAddRuleButton: {
                root: { className: "justify-center w-full min-[0px]:text-sm" },
                label: { className: "flex-auto grow-0" },
                icon: { className: "mr-2" },
            },
            filterButtonBar: {
                className:
                    "flex items-center justify-between p-4 border-t border-neutral-100 dark:border-neutral-700/50",
            },
            filterClearButton: {
                root: {
                    className:
                        "w-auto min-[0px]:text-sm px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors cursor-pointer",
                },
            },
            filterApplyButton: {
                root: {
                    className:
                        "w-auto min-[0px]:text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors cursor-pointer",
                },
            },
            filterRowItems: {
                className: "m-0 p-0 py-2 list-none",
            },
            filterRowItem: ({ context }: any) => ({
                className: classNames(
                    "m-0 py-2.5 px-4 cursor-pointer transition-colors",
                    {
                        "text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20":
                            context?.highlighted,
                        "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700":
                            !context?.highlighted,
                    },
                ),
            }),
            filterSeparator: {
                className: "border-t border-neutral-200 dark:border-neutral-700 my-1",
            },
        },
    },

    /* ── Paginator ── */
    paginator: {
        root: {
            className:
                "flex items-center justify-center flex-wrap gap-1 px-6 py-4 bg-neutral-50 dark:bg-neutral-900/50 border-t border-neutral-200 dark:border-neutral-800",
        },
        firstPageButton: {
            className:
                "inline-flex items-center justify-center w-8 h-8 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
        },
        previousPageButton: {
            className:
                "inline-flex items-center justify-center w-8 h-8 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
        },
        nextPageButton: {
            className:
                "inline-flex items-center justify-center w-8 h-8 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
        },
        lastPageButton: {
            className:
                "inline-flex items-center justify-center w-8 h-8 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
        },
        pageButton: ({ context }: any) => ({
            className: classNames(
                "inline-flex items-center justify-center min-w-[2rem] h-8 px-3 py-1 rounded-md text-sm font-medium transition-colors",
                {
                    "bg-primary-600 text-white shadow-sm": context?.active,
                    "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800":
                        !context?.active,
                },
            ),
        }),
        rowPerPageDropdown: {
            root: {
                className:
                    "inline-flex items-center border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 ml-2 text-sm",
            },
        },
        current: {
            className: "text-sm text-neutral-500 dark:text-neutral-400 mx-3",
        },
    },

    /* ── Dialog ── */
    dialog: {
        root: {
            className: "rounded-2xl shadow-2xl border border-neutral-200/50 dark:border-neutral-800/50 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl overflow-hidden",
        },
        mask: {
            className: "bg-gradient-to-br from-neutral-900/80 via-neutral-800/75 to-neutral-900/80 dark:from-black/90 dark:via-black/85 dark:to-black/90 backdrop-blur-md",
        },
        header: {
            className:
                "flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800",
        },
        headerTitle: {
            className: "text-lg font-semibold text-neutral-900 dark:text-neutral-100",
        },
        headerIcons: {
            className: "flex items-center gap-1",
        },
        closeButton: {
            className:
                "inline-flex items-center justify-center w-8 h-8 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:text-neutral-200 dark:hover:bg-neutral-800 transition-colors",
        },
        content: {
            className: "px-6 py-4 overflow-y-auto text-neutral-700 dark:text-neutral-300",
        },
        footer: {
            className:
                "flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 rounded-b-2xl",
        },
    },

    /* ── ConfirmDialog ── */
    confirmdialog: {
        root: {
            className: "rounded-2xl shadow-2xl border border-neutral-200/50 dark:border-neutral-800/50 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl overflow-hidden",
        },
        message: {
            className: "ml-4 text-neutral-700 dark:text-neutral-300",
        },
        icon: {
            className: "text-2xl",
        },
        acceptButton: {
            className:
                "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200",
        },
        rejectButton: {
            className:
                "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-white border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-all duration-200",
        },
    },

    /* ── Dropdown ── */
    dropdown: {
        root: ({ props }: any) => ({
            className: classNames(
                "inline-flex items-center cursor-pointer relative select-none",
                "rounded-lg border bg-white dark:bg-neutral-900 transition-all duration-200",
                {
                    "border-neutral-300 dark:border-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-600":
                        !props?.invalid,
                    "border-danger-500 dark:border-danger-400": props?.invalid,
                    "opacity-50 cursor-not-allowed": props?.disabled,
                },
            ),
        }),
        input: {
            className:
                "flex-auto overflow-hidden text-ellipsis whitespace-nowrap px-3 py-2.5 text-sm text-neutral-900 dark:text-neutral-100 bg-transparent outline-none cursor-pointer",
        },
        trigger: {
            className:
                "flex items-center justify-center px-2 text-neutral-500 dark:text-neutral-400",
        },
        panel: {
            className:
                "bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 mt-1 overflow-hidden",
        },
        header: {
            className: "border-b border-neutral-200 dark:border-neutral-700",
        },
        filterContainer: {
            className: "flex items-center relative px-3 py-2",
        },
        filterInput: {
            root: {
                className:
                    "w-full pl-8 pr-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500",
            },
        },
        filterIcon: {
            className: "absolute left-5.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400",
        },
        wrapper: {
            className: "max-h-52 overflow-auto",
        },
        list: {
            className: "py-1",
        },
        item: ({ context }: any) => ({
            className: classNames(
                "px-3 py-2.5 text-sm cursor-pointer transition-colors",
                {
                    "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400":
                        context?.selected,
                    "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700":
                        !context?.selected,
                },
            ),
        }),
        emptyMessage: {
            className: "px-3 py-2.5 text-sm text-neutral-500 dark:text-neutral-400",
        },
    },

    /* ── InputText ── */
    inputtext: {
        root: ({ props, context }: any) => ({
            className: classNames(
                "w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 outline-none transition-all duration-200",
                {
                    "border-neutral-300 dark:border-neutral-700 focus:border-transparent focus:ring-2 focus:ring-primary-500":
                        !props?.invalid,
                    "border-danger-500 dark:border-danger-400 focus:ring-2 focus:ring-danger-500":
                        props?.invalid,
                    "opacity-50 cursor-not-allowed": context?.disabled,
                },
            ),
        }),
    },

    /* ── InputTextarea ── */
    inputtextarea: {
        root: ({ props }: any) => ({
            className: classNames(
                "w-full px-3 py-2.5 text-sm rounded-lg border bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 outline-none transition-colors resize-y",
                {
                    "border-neutral-300 dark:border-neutral-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20":
                        !props?.invalid,
                    "border-danger-500 focus:ring-2 focus:ring-danger-500/20": props?.invalid,
                },
            ),
        }),
    },

    /* ── InputNumber ── */
    inputnumber: {
        root: {
            className: "inline-flex items-center",
        },
        input: {
            root: ({ props }: any) => ({
                className: classNames(
                    "w-full px-3 py-2.5 text-sm rounded-lg border bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 outline-none transition-colors",
                    {
                        "border-neutral-300 dark:border-neutral-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20":
                            !props?.invalid,
                    },
                ),
            }),
        },
        buttonGroup: {
            className: "flex flex-col ml-1",
        },
        incrementButton: {
            className:
                "flex items-center justify-center w-8 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-t-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors",
        },
        decrementButton: {
            className:
                "flex items-center justify-center w-8 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-b-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors",
        },
    },

    /* ── InputSwitch ── */
    inputswitch: {
        root: ({ props }: any) => ({
            className: classNames(
                "inline-block relative w-12 h-7 rounded-full transition-colors duration-200 cursor-pointer",
                {
                    "bg-primary-500": props?.checked,
                    "bg-neutral-300 dark:bg-neutral-600": !props?.checked,
                },
            ),
        }),
        input: {
            className: "absolute w-full h-full top-0 left-0 opacity-0 z-10 cursor-pointer m-0 p-0 appearance-none",
        },
        slider: ({ props }: any) => ({
            className: classNames(
                "absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200",
                {
                    "translate-x-5": props?.checked,
                    "translate-x-0": !props?.checked,
                },
            ),
        }),
    },

    /* ── Calendar ── */
    calendar: {
        root: {
            className: "inline-flex items-center relative max-w-full",
        },
        input: {
            className:
                "w-full px-3 py-2.5 text-sm rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-colors",
        },
        dropdownButton: {
            className:
                "flex h-[42px] w-12 shrink-0 items-center justify-center rounded-r-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors",
        },
        panel: {
            className:
                "w-72 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 p-3",
        },
        group: {
            className: "w-full",
        },
        header: {
            className: "flex items-center justify-between mb-2",
        },
        title: {
            className: "flex items-center gap-1 font-semibold text-neutral-900 dark:text-neutral-100 mx-auto",
        },
        monthTitle: {
            className: "font-semibold text-neutral-900 dark:text-neutral-100",
        },
        yearTitle: {
            className: "font-semibold text-neutral-900 dark:text-neutral-100",
        },
        decadeTitle: {
            className: "font-semibold text-neutral-900 dark:text-neutral-100",
        },
        previousButton: {
            className:
                "w-8 h-8 flex items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors",
        },
        nextButton: {
            className:
                "w-8 h-8 flex items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors",
        },
        table: {
            className: "w-full table-fixed border-collapse",
        },
        tableHeaderCell: {
            className: "h-8 text-center",
        },
        weekDay: {
            className: "text-xs font-semibold text-neutral-500 dark:text-neutral-400",
        },
        day: {
            className: "p-0 text-center align-middle",
        },
        dayLabel: ({ context }: any) => ({
            className: classNames(
                "mx-auto flex h-8 w-8 items-center justify-center rounded-lg text-sm transition-colors",
                {
                    "bg-primary-500 text-white font-semibold": context?.selected,
                    "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700":
                        !context?.selected && !context?.disabled,
                    "text-neutral-300 dark:text-neutral-600": context?.disabled,
                },
            ),
        }),
        monthPicker: {
            className: "grid grid-cols-3 gap-2 py-2",
        },
        month: ({ context }: any) => ({
            className: classNames(
                "flex h-10 items-center justify-center rounded-lg text-sm transition-colors",
                {
                    "bg-primary-500 text-white font-semibold": context?.selected,
                    "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700":
                        !context?.selected && !context?.disabled,
                    "text-neutral-300 dark:text-neutral-600": context?.disabled,
                },
            ),
        }),
        yearPicker: {
            className: "grid grid-cols-2 gap-2 py-2",
        },
        year: ({ context }: any) => ({
            className: classNames(
                "flex h-10 items-center justify-center rounded-lg text-sm transition-colors",
                {
                    "bg-primary-500 text-white font-semibold": context?.selected,
                    "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700":
                        !context?.selected && !context?.disabled,
                    "text-neutral-300 dark:text-neutral-600": context?.disabled,
                },
            ),
        }),
    },

    /* ── MultiSelect ── */
    multiselect: {
        root: ({ props }: any) => ({
            className: classNames(
                "inline-flex items-center cursor-pointer relative select-none rounded-lg border bg-white dark:bg-neutral-800 transition-colors",
                {
                    "border-neutral-300 dark:border-neutral-600": !props?.invalid,
                    "border-danger-500": props?.invalid,
                },
            ),
        }),
        labelContainer: {
            className: "overflow-hidden flex-auto cursor-pointer",
        },
        label: {
            className: "px-3 py-2.5 text-sm text-neutral-900 dark:text-neutral-100",
        },
        trigger: {
            className: "flex items-center justify-center px-2 text-neutral-500",
        },
        panel: {
            className:
                "bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 mt-1 overflow-hidden",
        },
        header: {
            className: "flex items-center gap-2 px-3 py-2.5 border-b border-neutral-200 dark:border-neutral-700",
        },
        headerCheckboxContainer: {
            className: "flex items-center",
        },
        filterContainer: {
            className: "flex-1 flex items-center relative",
        },
        filterInput: {
            root: {
                className:
                    "w-full pl-8 pr-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500",
            },
        },
        filterIcon: {
            className: "absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400",
        },
        closeButton: {
            className:
                "ml-auto w-7 h-7 flex items-center justify-center rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:text-neutral-300 dark:hover:bg-neutral-700 transition-colors",
        },
        closeIcon: {
            className: "w-3.5 h-3.5",
        },
        wrapper: {
            className: "max-h-52 overflow-auto",
        },
        list: {
            className: "py-1",
        },
        item: ({ context }: any) => ({
            className: classNames(
                "px-3 py-2.5 text-sm cursor-pointer transition-colors flex items-center gap-2",
                {
                    "bg-primary-50 dark:bg-primary-900/20": context?.selected,
                    "hover:bg-neutral-100 dark:hover:bg-neutral-700": !context?.selected,
                },
            ),
        }),
        checkboxContainer: {
            className: "flex items-center mr-1",
        },
        token: {
            className: "inline-flex items-center gap-1 px-2 py-0.5 mr-1 mb-0.5 text-xs font-medium rounded-md bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300",
        },
        tokenLabel: {
            className: "",
        },
        removeTokenIcon: {
            className: "w-3 h-3 cursor-pointer hover:text-primary-900 dark:hover:text-primary-100",
        },
        clearIcon: {
            className: "w-4 h-4 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 mr-1",
        },
    },

    /* ── Chips ── */
    chips: {
        root: {
            className: "flex w-full",
        },
        container: {
            className:
                "flex flex-wrap items-center gap-1 w-full min-h-[42px] px-2 py-1.5 m-0 list-none rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 focus-within:border-transparent focus-within:ring-2 focus-within:ring-primary-500 transition-all duration-200 cursor-text",
        },
        token: {
            className: "inline-flex items-center gap-1 max-w-full px-2 py-0.5 text-xs font-medium rounded-md bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300",
        },
        label: {
            className: "break-all",
        },
        removeTokenIcon: {
            className: "w-3 h-3 shrink-0 cursor-pointer hover:text-primary-900 dark:hover:text-primary-100",
        },
        inputToken: {
            className: "flex flex-1 min-w-[8rem]",
        },
        input: {
            className: "w-full py-1 text-sm bg-transparent border-0 outline-none text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400",
        },
    },

    /* ── TabView ── */
    tabview: {
        root: {
            className: "",
        },
        navContainer: {
            className: "px-5 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-800",
        },
        nav: {
            className:
                "flex items-center gap-1 overflow-x-auto hide-scrollbar",
        },
        tab: {
            header: ({ context }: any) => ({
                className: classNames(
                    "group flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors duration-200 cursor-pointer",
                    {
                        "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400":
                            context?.active,
                        "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-100":
                            !context?.active,
                    },
                ),
            }),
            headerAction: {
                className: "flex items-center gap-2",
            },
            headerTitle: {
                className: "",
            },
            content: {
                className: "pt-4",
            },
        },
    },

    /* ── Toast ── */
    toast: {
        root: {
            className: "w-96 max-w-full",
        },
        message: ({ state, index }: any) => {
            const severity = state?.messages?.[index]?.message?.severity;
            return {
                className: classNames(
                    "flex items-start gap-3 p-4 mb-2 rounded-xl border-l-4 shadow-lg backdrop-blur-sm",
                    {
                        "bg-info-100 border-info-500 text-info-900 dark:bg-info-900/40 dark:border-info-400 dark:text-info-100":
                            severity === "info",
                        "bg-success-100 border-success-500 text-success-900 dark:bg-success-900/40 dark:border-success-400 dark:text-success-100":
                            severity === "success",
                        "bg-warning-100 border-warning-500 text-warning-900 dark:bg-warning-900/40 dark:border-warning-400 dark:text-warning-100":
                            severity === "warn",
                        "bg-danger-100 border-danger-500 text-danger-900 dark:bg-danger-900/40 dark:border-danger-400 dark:text-danger-100":
                            severity === "error",
                    },
                ),
            };
        },
        content: {
            className: "flex items-start gap-3 flex-1",
        },
        icon: ({ state, index }: any) => {
            const severity = state?.messages?.[index]?.message?.severity;
            return {
                className: classNames("flex-shrink-0 w-5 h-5 mt-0.5", {
                    "text-info-600 dark:text-info-300": severity === "info",
                    "text-success-600 dark:text-success-300": severity === "success",
                    "text-warning-600 dark:text-warning-300": severity === "warn",
                    "text-danger-600 dark:text-danger-300": severity === "error",
                }),
            };
        },
        text: {
            className: "flex flex-col flex-1",
        },
        summary: {
            className: "font-semibold text-sm",
        },
        detail: {
            className: "text-sm mt-1 opacity-80",
        },
        closeButton: {
            className:
                "flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10 transition-colors",
        },
    },

    /* ── Sidebar ── */
    sidebar: {
        root: ({ props }: any) => ({
            className: classNames(
                "flex flex-col bg-white dark:bg-neutral-900 shadow-2xl border-r border-neutral-200 dark:border-neutral-800",
                {
                    "w-80": props?.position === "left" || props?.position === "right",
                },
            ),
        }),
        mask: {
            className: "bg-black/50 backdrop-blur-sm",
        },
        header: {
            className: "flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800",
        },
        closeButton: {
            className:
                "w-8 h-8 flex items-center justify-center rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors",
        },
        content: {
            className: "flex-1 overflow-y-auto p-4",
        },
    },

    /* ── Accordion ── */
    accordion: {
        root: {
            className: "rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden",
        },
    },

    accordiontab: {
        root: {
            className: "border-b border-neutral-200 dark:border-neutral-800 last:border-b-0",
        },
        header: {
            className: "",
        },
        headerAction: ({ context }: any) => ({
            className: classNames(
                "flex items-center justify-between w-full px-4 py-3 text-sm font-medium cursor-pointer transition-colors",
                {
                    "bg-neutral-50 dark:bg-neutral-800/50 text-neutral-900 dark:text-neutral-100": context?.active,
                    "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/30": !context?.active,
                },
            ),
        }),
        headerTitle: {
            className: "",
        },
        toggleIcon: {
            className: "text-neutral-500 dark:text-neutral-400",
        },
        content: {
            className: "px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300",
        },
    },

    /* ── Fieldset ── */
    fieldset: {
        root: {
            className: "rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900",
        },
        legend: {
            className:
                "px-3 py-1 text-sm font-semibold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-800",
        },
        content: {
            className: "p-4",
        },
    },

    /* ── ProgressBar ── */
    progressbar: {
        root: {
            className: "h-2 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-700",
        },
        value: {
            className: "h-full bg-primary-500 rounded-full transition-all duration-300",
        },
        label: {
            className: "text-xs font-semibold text-white",
        },
    },

    /* ── ProgressSpinner ── */
    progressspinner: {
        root: {
            className: "inline-block",
        },
        circle: {
            className: "stroke-primary-500",
        },
    },

    /* ── Tag ── */
    tag: {
        root: ({ props }: any) => ({
            className: classNames(
                "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold",
                {
                    "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400":
                        !props?.severity || props?.severity === "info",
                    "bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400":
                        props?.severity === "success",
                    "bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400":
                        props?.severity === "danger",
                    "bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400":
                        props?.severity === "warning",
                },
            ),
        }),
    },

    /* ── Checkbox ── */
    checkbox: {
        root: {
            className: "inline-flex items-center cursor-pointer relative",
        },
        input: {
            className: "absolute w-full h-full top-0 left-0 opacity-0 z-10 cursor-pointer m-0 p-0 appearance-none",
        },
        box: ({ context }: any) => ({
            className: classNames(
                "flex items-center justify-center w-5 h-5 rounded border transition-colors",
                {
                    "bg-primary-500 border-primary-500 text-white": context?.checked,
                    "border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800": !context?.checked,
                },
            ),
        }),
        icon: {
            className: "w-3 h-3 text-white",
        },
    },

    /* ── SplitButton ── */
    splitbutton: {
        root: {
            className: "inline-flex",
        },
        button: {
            root: {
                className:
                    "inline-flex items-center justify-center gap-2 rounded-l-xl rounded-r-none px-4 py-2.5 text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200",
            },
        },
        menuButton: {
            root: {
                className:
                    "inline-flex items-center justify-center rounded-r-xl rounded-l-none px-2.5 py-2.5 bg-primary-500 text-white hover:bg-primary-600 border-l border-primary-400/40 transition-all duration-200",
            },
        },
        /* menu → forwarded as TieredMenu pt */
        menu: {
            root: {
                className:
                    "bg-white dark:bg-neutral-800 rounded-xl shadow-xl ring-1 ring-neutral-200 dark:ring-neutral-700 mt-1.5 overflow-hidden min-w-[12rem]",
            },
            menu: {
                className: "list-none m-0 p-1.5",
            },
            menuitem: {
                className: "",
            },
            content: {
                className: "rounded-lg",
            },
            action: {
                className:
                    "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700/60 cursor-pointer transition-colors no-underline",
            },
            icon: {
                className: "text-neutral-500 dark:text-neutral-400 shrink-0",
            },
            label: {
                className: "font-medium",
            },
        },
    },

    /* ── TabMenu ── */
    tabmenu: {
        root: {
            className: "",
        },
        menu: {
            className: "flex items-center gap-1 overflow-x-auto hide-scrollbar",
        },
        menuitem: ({ context }: any) => ({
            className: classNames(
                "group flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors duration-200 cursor-pointer",
                {
                    "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400":
                        context?.active,
                    "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-neutral-100":
                        !context?.active,
                },
            ),
        }),
    },

    /* ── Tooltip ── */
    tooltip: {
        root: {
            className:
                "px-3 py-2 text-sm bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 rounded-lg shadow-lg",
        },
        arrow: {
            className: "",
        },
    },

    /* ── FileUpload ── */
    fileupload: {
        root: {
            className: "",
        },
        buttonbar: {
            className: "flex items-center gap-2 p-4",
        },
        content: {
            className: "p-4 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl bg-neutral-50 dark:bg-neutral-800/50",
        },
    },

    /* ── Messages ── */
    messages: {
        root: {
            className: "rounded-xl overflow-hidden",
        },
        wrapper: {
            className: "flex items-center gap-3 p-4",
        },
        icon: {
            className: "text-xl flex-shrink-0",
        },
        text: {
            className: "text-sm",
        },
    },

    /* ── TriStateCheckbox ── */
    tristatecheckbox: {
        root: {
            className: "inline-flex items-center cursor-pointer",
        },
        checkbox: ({ props }: any) => ({
            className: classNames(
                "flex items-center justify-center w-5 h-5 rounded border transition-colors",
                {
                    "bg-primary-500 border-primary-500 text-white": props?.value === true,
                    "bg-neutral-500 border-neutral-500 text-white": props?.value === false,
                    "border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800": props?.value === null,
                },
            ),
        }),
    },

    /* ── Carousel ── */
    carousel: {
        root: {
            className: "flex flex-col",
        },
        content: {
            className: "flex flex-col overflow-hidden",
        },
        container: {
            className: "relative flex",
        },
        previousButton: {
            className:
                "w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-neutral-800 shadow-md text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors",
        },
        nextButton: {
            className:
                "w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-neutral-800 shadow-md text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors",
        },
        indicatorsContent: {
            className: "flex justify-center gap-2 mt-4",
        },
        indicator: ({ context }: any) => ({
            className: classNames("w-2 h-2 rounded-full transition-colors cursor-pointer", {
                "bg-primary-500": context?.active,
                "bg-neutral-300 dark:bg-neutral-600": !context?.active,
            }),
        }),
    },
};
